/**
 * Integration catalog — every connectable provider (store, attribution, ESP,
 * accounting, …) fetched from the backend registry (the single source of
 * truth). The Connections page, the connect modal, the onboarding connect
 * drawer, and the SyncPill all render from this — so adding a provider
 * server-side makes it appear everywhere with the right fields, sync buttons,
 * and labels, and NO frontend changes.
 */

import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "./api-context.js";

export interface CatalogField {
  key: string;
  label: string;
  secret: boolean;
  placeholder?: string;
  help?: string;
  /** May be left blank (server-side validation decides which combos work). */
  optional?: boolean;
}

export interface CatalogSyncAction {
  id: string;
  label: string;
  pendingLabel: string;
  primary: boolean;
  /** One-line plain-words explanation shown under the action's button. */
  description: string;
  /** Long-running full re-pull — the UI confirms before starting. */
  heavy: boolean;
}

export interface CatalogProvider {
  platform: string;
  category: string;
  label: string;
  description: string;
  required: boolean;
  comingSoon: boolean;
  /**
   * A familiar platform we can identify during onboarding but do not yet pull
   * from directly. It stays visible so the client can tell us they use it;
   * the connection screen then says plainly that our team will review it.
   */
  reviewWithUs?: boolean;
  connectKind: "esp" | "fields" | "bespoke" | "coming_soon";
  showInOnboarding: boolean;
  autoSyncHours?: number;
  /** Credential fields + instructions (connectKind "esp" or "fields"). */
  instructions?: string;
  fields?: CatalogField[];
  /** GET here for a login-button OAuth start URL (e.g. Meta), when configured. */
  oauthUrlPath?: string;
  /** More than one sign-in identity for the same provider (Microsoft Ads can
   * use a Google or Microsoft login). Each path stays server-owned. */
  oauthChoices?: Array<{ id: string; label: string; urlPath: string }>;
  /** If set, the login URL needs this field's value as a query param
   *  (e.g. Shopify's store domain) — sent when starting OAuth. */
  oauthShopParam?: { fromFieldKey: string; queryKey: string };
  /** Generalized: several fields can ride into one login URL. */
  oauthFieldParams?: Array<{ fromFieldKey: string; queryKey: string }>;
  /** Login is the only connect path for this platform. */
  oauthOnly?: boolean;
  /** Login-only platform whose OAuth app isn't configured yet. */
  oauthPending?: boolean;
  /** We-handle-it connection: request-button-first modal. */
  whiteGlove?: boolean;
  /** OAuth exists in code but isn't configured yet (dual-mode platforms). */
  oauthPlanned?: boolean;
  /** While the login is planned-but-off, lead with "request a connection
   *  link" because manual setup needs developer access clients won't have
   *  (TikTok). Auto-clears once the login is configured. */
  requestFirst?: boolean;
  /** Push capabilities (presence-based). A dual-role platform (Ontraport) is
   *  both — the Connections page lists it under Email AND CRM. */
  isEsp?: boolean;
  isCrm?: boolean;
  /** Manual sync buttons for the connected card. */
  syncActions: CatalogSyncAction[];
}

export interface IntegrationCatalog {
  providers: CatalogProvider[];
  jobLabels: Record<string, string>;
}

export function useIntegrationCatalog(enabled = true) {
  const api = useApiClient();
  return useQuery({
    queryKey: ["integration-catalog"],
    enabled,
    queryFn: () => api.get<IntegrationCatalog>("/integrations/catalog"),
    staleTime: 1000 * 60 * 60, // static config — refetch rarely
  });
}

/**
 * The account's currently-connected ESP (email platform), or null. Used to
 * label + target the "Push to <ESP>" action on segments. Derives the ESP set
 * from the catalog (category === "esp") so it works for any registered ESP.
 */
export function useConnectedEsp(): { platform: string; label: string } | null {
  const api = useApiClient();
  const { data: catalog } = useIntegrationCatalog();
  const { data } = useQuery({
    queryKey: ["integrations"],
    queryFn: () => api.get<{ integrations: Array<{ platform: string; status: string }> }>("/integrations"),
  });
  if (!catalog || !data) return null;
  const espLabels = new Map(
    catalog.providers.filter((p) => p.category === "esp").map((p) => [p.platform, p.label] as const),
  );
  const active = data.integrations.find((i) => i.status === "active" && espLabels.has(i.platform));
  return active ? { platform: active.platform, label: espLabels.get(active.platform) ?? active.platform } : null;
}

/** Founder-friendly section headers for the provider categories. Shared by the
 *  Connections page AND the onboarding connect drawer so both group identically
 *  (founder 2026-07-07: onboarding connect "has to be grouped like the
 *  connections page — right now it's just a ton of connections"). */
export const CATEGORY_META: Record<string, { label: string; blurb: string; order: number }> = {
  store: { label: "Stores & Order Sources", blurb: "Where your orders live — connect at least one. This is the revenue source of truth.", order: 1 },
  subscription_state: { label: "Subscription Apps", blurb: "MRR, churn, next bills, and failed payments. Rebills stay with your store connection — nothing double-counts.", order: 2 },
  ad_platform: { label: "Ad Platforms", blurb: "Daily ad spend per campaign. Powers CAC and MER by channel.", order: 3 },
  attribution: { label: "Affiliate & Attribution", blurb: "Affiliate conversions and commissions — real acquisition costs per order.", order: 4 },
  cost_actualization: { label: "Real Costs (Fees & Shipping)", blurb: "Replaces estimated processing fees and shipping with the real settled numbers. Never pulls orders.", order: 5 },
  esp: { label: "Email & SMS", blurb: "Push your customer segments out to the tools that message them.", order: 6 },
  crm: { label: "CRM", blurb: "Pull orders in when your CRM is your system of record, and push customer profit data back.", order: 7 },
  accounting: { label: "Accounting", blurb: "Monthly overhead from your books — powers operating profit.", order: 8 },
  analytics: { label: "Analytics", blurb: "Page-level and funnel behavior your store can't see. Your orders stay the revenue source of truth.", order: 9 },
  _other: { label: "More", blurb: "", order: 99 },
};

/** Group providers by their registry category, in the founder-friendly order.
 *  `rank` orders WITHIN a group (e.g. connected-first). Dual-role platforms
 *  (Ontraport = email + CRM) appear in both their category sections. Returns
 *  ordered [category, providers] entries. */
export function groupProviders(
  providers: CatalogProvider[],
  rank: (p: CatalogProvider) => number = () => 0,
): Array<[string, CatalogProvider[]]> {
  const groups = new Map<string, CatalogProvider[]>();
  const addTo = (key: string, p: CatalogProvider): void => {
    const list = groups.get(key) ?? [];
    list.push(p);
    groups.set(key, list);
  };
  for (const p of providers) {
    const primary = CATEGORY_META[p.category] ? p.category : "_other";
    addTo(primary, p);
    if (p.isCrm && primary !== "crm") addTo("crm", p);
    if (p.isEsp && primary !== "esp") addTo("esp", p);
  }
  for (const list of groups.values()) list.sort((a, b) => rank(a) - rank(b) || a.label.localeCompare(b.label));
  return [...groups.entries()].sort((a, b) => (CATEGORY_META[a[0]]?.order ?? 99) - (CATEGORY_META[b[0]]?.order ?? 99));
}
